/**
 * Per-ticket live quote metrics for open positions UI (P&L, change, current price).
 */

import { create } from "zustand";
import type { OpenedTrade } from "../types";
import type { SubscribedSymbolEntry } from "../../subscribed-symbols/types";
import type { Symbol } from "../../symbols/types";
import {
  calculateTradeAccountPnl,
  getTradeClosePrice,
  getTradePriceChange,
  isMarketTrade,
} from "../utils";

export interface OpenPositionLiveMetrics {
  symbol: string;
  currentPrice: number;
  priceChange: number;
  profit: number;
}

interface ComputeContext {
  accountCurrency: string;
  symbols: Map<string, Symbol>;
  quotes: Map<string, SubscribedSymbolEntry>;
}

function computeMetrics(
  trade: OpenedTrade,
  ctx: ComputeContext
): OpenPositionLiveMetrics {
  const quote = ctx.quotes.get(trade.symbol)?.quote;
  return {
    symbol: trade.symbol,
    currentPrice: getTradeClosePrice(trade, quote),
    priceChange: getTradePriceChange(trade, quote),
    profit: calculateTradeAccountPnl({
      trade,
      symbol: ctx.symbols.get(trade.symbol),
      quote,
      accountCurrency: ctx.accountCurrency,
      symbols: ctx.symbols,
      quotes: ctx.quotes,
    }),
  };
}

function metricsEqual(
  a: OpenPositionLiveMetrics | undefined,
  b: OpenPositionLiveMetrics
): boolean {
  if (!a) return false;
  return (
    a.symbol === b.symbol &&
    a.currentPrice === b.currentPrice &&
    a.priceChange === b.priceChange &&
    a.profit === b.profit
  );
}

interface OpenPositionsLiveMetricsState {
  byTicket: Map<number, OpenPositionLiveMetrics>;
  version: number;
  setMetrics: (ticket: number, metrics: OpenPositionLiveMetrics) => void;
  removeTicket: (ticket: number) => void;
  clear: () => void;
  syncTradeList: (trades: OpenedTrade[]) => void;
  recomputeAll: (trades: OpenedTrade[], ctx: ComputeContext) => void;
  recomputeForSymbol: (
    symbol: string,
    trades: OpenedTrade[],
    ctx: ComputeContext
  ) => void;
}

export const useOpenPositionsLiveMetricsStore =
  create<OpenPositionsLiveMetricsState>((set, get) => ({
    byTicket: new Map(),
    version: 0,

    setMetrics: (ticket, metrics) => {
      const prev = get().byTicket.get(ticket);
      if (metricsEqual(prev, metrics)) return;
      const next = new Map(get().byTicket);
      next.set(ticket, metrics);
      set({ byTicket: next, version: get().version + 1 });
    },

    removeTicket: (ticket) => {
      if (!get().byTicket.has(ticket)) return;
      const next = new Map(get().byTicket);
      next.delete(ticket);
      set({ byTicket: next, version: get().version + 1 });
    },

    clear: () => set({ byTicket: new Map(), version: 0 }),

    syncTradeList: (trades) => {
      const marketTickets = new Set(
        trades.filter(isMarketTrade).map((t) => t.ticket)
      );
      const next = new Map(get().byTicket);
      let changed = false;
      for (const ticket of next.keys()) {
        if (!marketTickets.has(ticket)) {
          next.delete(ticket);
          changed = true;
        }
      }
      if (changed) {
        set({ byTicket: next, version: get().version + 1 });
      }
    },

    recomputeAll: (trades, ctx) => {
      const next = new Map(get().byTicket);
      let changed = false;
      for (const trade of trades) {
        if (!isMarketTrade(trade)) continue;
        const metrics = computeMetrics(trade, ctx);
        const prev = next.get(trade.ticket);
        if (!metricsEqual(prev, metrics)) {
          next.set(trade.ticket, metrics);
          changed = true;
        }
      }
      if (changed) {
        set({ byTicket: next, version: get().version + 1 });
      }
    },

    recomputeForSymbol: (symbol, trades, ctx) => {
      const next = new Map(get().byTicket);
      let changed = false;
      for (const trade of trades) {
        if (trade.symbol !== symbol || !isMarketTrade(trade)) continue;
        const metrics = computeMetrics(trade, ctx);
        const prev = next.get(trade.ticket);
        if (!metricsEqual(prev, metrics)) {
          next.set(trade.ticket, metrics);
          changed = true;
        }
      }
      if (changed) {
        set({ byTicket: next, version: get().version + 1 });
      }
    },
  }));

export function selectOpenPositionMetrics(ticket: number) {
  return (state: OpenPositionsLiveMetricsState) =>
    state.byTicket.get(ticket);
}

/** Net open P&L for a symbol, or null when the symbol holds no live ticket. */
export interface SymbolPnl {
  profit: number;
  ticketCount: number;
}

/**
 * Sums live `profit` across every ticket whose metrics carry `symbol`. Memoized
 * on the store `version` so a stable reference is returned between ticks that
 * don't change the map — required by zustand to avoid re-render loops.
 */
export function selectSymbolPnl(symbol: string) {
  let cachedVersion = -1;
  let cachedResult: SymbolPnl | null = null;

  return (state: OpenPositionsLiveMetricsState): SymbolPnl | null => {
    if (state.version === cachedVersion) {
      return cachedResult;
    }
    cachedVersion = state.version;

    let profit = 0;
    let ticketCount = 0;
    for (const metrics of state.byTicket.values()) {
      if (metrics.symbol !== symbol) continue;
      profit += metrics.profit;
      ticketCount += 1;
    }

    cachedResult = ticketCount === 0 ? null : { profit, ticketCount };
    return cachedResult;
  };
}

export function metricsAreEqual(
  a: OpenPositionLiveMetrics | undefined,
  b: OpenPositionLiveMetrics | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return metricsEqual(a, b);
}

export function computeOpenPositionSummary(
  byTicket: Map<number, OpenPositionLiveMetrics>
): { totalProfit: number; profitable: number; losing: number } {
  let totalProfit = 0;
  let profitable = 0;
  let losing = 0;
  for (const m of byTicket.values()) {
    totalProfit += m.profit;
    if (m.profit > 0) profitable += 1;
    else if (m.profit < 0) losing += 1;
  }
  return { totalProfit, profitable, losing };
}
